import { useState, useEffect } from 'react';
import translationService from '../../services/translationService';

/**
 * useTranslatedPreferences Hook
 *
 * Fetches translated entity preference values (luggage warning, verification prompt).
 * Handles language changes and falls back to default values if translation unavailable.
 *
 * State Ownership:
 * - Owns: translatedLuggageWarningMessage, translatedVerificationPrompt
 *
 * Integration Points:
 * - Reads: entityId, language from parent
 * - Reads: default values and metadata from entitySettings
 *
 * @param {Object} params
 * @param {number} params.entityId - Current entity ID
 * @param {string} params.language - Current language code (from i18n)
 * @param {string} params.defaultLuggageWarning - Default luggage warning message
 * @param {string} params.defaultVerificationPrompt - Default verification prompt
 * @param {Object} params.entitySettings - Entity settings with preference metadata
 * @param {boolean} params.multiLanguageEnabled - Whether multi-language is enabled
 * @returns {Object} Translated preference values
 */
const useTranslatedPreferences = ({
  entityId,
  language,
  defaultLuggageWarning,
  defaultVerificationPrompt,
  entitySettings,
  multiLanguageEnabled
}) => {
  const [translatedLuggageWarningMessage, setTranslatedLuggageWarningMessage] = useState(
    defaultLuggageWarning
  );
  const [translatedVerificationPrompt, setTranslatedVerificationPrompt] = useState(
    defaultVerificationPrompt
  );
  const defaultLanguage = entitySettings?.entity_default_language || entitySettings?.default_language || 'en';
  const shouldTranslateContent = multiLanguageEnabled === true && language !== defaultLanguage;

  /**
   * Fetch translated luggage warning message
   */
  useEffect(() => {
    const fetchTranslatedLuggageWarning = async () => {
      if (!shouldTranslateContent || !defaultLuggageWarning || !entityId) {
        setTranslatedLuggageWarningMessage(defaultLuggageWarning);
        return;
      }

      // Need the preference record ID to fetch translations
      const preferenceMetadata = entitySettings?.preferenceMetadata?.maximum_luggage_per_rider_warning_message;

      if (!preferenceMetadata?.id) {
        setTranslatedLuggageWarningMessage(defaultLuggageWarning);
        return;
      }

      try {
        const translatedValue = await translationService.getTranslatedContent(
          entityId,
          'entity_preference',
          preferenceMetadata.id,
          'preference_value',
          language,
          defaultLuggageWarning
        );
        setTranslatedLuggageWarningMessage(translatedValue?.content || defaultLuggageWarning);
      } catch (error) {
        console.warn('Failed to fetch translated luggage warning, using default:', error);
        setTranslatedLuggageWarningMessage(defaultLuggageWarning);
      }
    };

    fetchTranslatedLuggageWarning();
  }, [shouldTranslateContent, defaultLuggageWarning, entitySettings, entityId, language]);

  /**
   * Fetch translated verification prompt
   */
  useEffect(() => {
    const fetchTranslatedVerificationPrompt = async () => {
      if (!shouldTranslateContent || !defaultVerificationPrompt || !entityId) {
        setTranslatedVerificationPrompt(defaultVerificationPrompt);
        return;
      }

      // Need the preference record ID to fetch translations
      const preferenceMetadata = entitySettings?.preferenceMetadata?.booking_additional_verification_prompt;

      if (!preferenceMetadata?.id) {
        setTranslatedVerificationPrompt(defaultVerificationPrompt);
        return;
      }

      try {
        const translatedValue = await translationService.getTranslatedContent(
          entityId,
          'entity_preference',
          preferenceMetadata.id,
          'preference_value',
          language,
          defaultVerificationPrompt
        );
        setTranslatedVerificationPrompt(translatedValue?.content || defaultVerificationPrompt);
      } catch (error) {
        console.warn('Failed to fetch translated verification prompt, using default:', error);
        setTranslatedVerificationPrompt(defaultVerificationPrompt);
      }
    };

    fetchTranslatedVerificationPrompt();
  }, [shouldTranslateContent, defaultVerificationPrompt, entitySettings, entityId, language]);

  return {
    translatedLuggageWarningMessage,
    translatedVerificationPrompt,
  };
};

export default useTranslatedPreferences;
